import { h, Fragment, Component } from "preact";
import { useState } from "preact/hooks"; // Added for local state in CampaignCard
import { Trans } from "@lingui/react/macro";
import User, { RolloverInfo, RolloverState, BonusCampaign, UserData } from "../../../../services/user";
import { formatDateTime } from "../../utils/date-formats";
import formatCurrency from "../../../../services/currency";
import { UpCaretIcon } from "../../utils/icons";
import Config from "../../../../services/config";

import * as U from "../../desktop-templates/menu-user";
import { route } from "preact-router";

const USE_MOCK_DATA = false; // Set to false to use API

// Define ProcessedCampaign type
type ProcessedCampaign = BonusCampaign & {
    rollovers: RolloverState[];
    totalActiveBonus: number;
};

// Simple ProgressBar component
interface ProgressBarProps {
    percentage: number; // 0 to 100
}

interface BonusStatusPageProps {
    // Define any props if needed, for now, it's empty
}

interface BonusStatusPageState {
    loading: boolean;
    error: string | null;
    rolloverData: RolloverInfo | null;
    userData: UserData | null;
}

export class BonusStatusPage extends Component<BonusStatusPageProps, BonusStatusPageState> {
    state = {
        loading: true,
        error: null,
        rolloverData: null,
        userData: User.data || null,
    } as BonusStatusPageState;

    async componentWillMount() {
        await this.fetchData();
    }

    fetchData = async () => {
        this.setState({ loading: true, error: null });
        try {
            let userDataToSet: UserData | null = this.state.userData;
            if (USE_MOCK_DATA) {
                if (!User.data) {
                    const sessionData = await User.isSessionOpen();
                    if (sessionData) userDataToSet = sessionData as UserData;
                } else {
                    userDataToSet = User.data;
                }
                this.setState({ rolloverData: getMockRolloverData(), userData: userDataToSet });
            } else {
                if (!User.data) {
                    const sessionData = await User.isSessionOpen();
                    if (sessionData) {
                        userDataToSet = sessionData as UserData;
                    } else {
                        throw new Error("User session not available.");
                    }
                } else {
                    userDataToSet = User.data;
                }

                const rollovers = await User.fetchRolloverStates();
                this.setState({ rolloverData: rollovers, userData: userDataToSet });
            }
        } catch (e: any) {
            console.error("Error fetching bonus status data:", e);
            this.setState({ error: e.message || "Failed to load bonus status. Please try again." });
        } finally {
            this.setState({ loading: false });
        }
    };

    getProcessedCampaigns = (): ProcessedCampaign[] => {
        const { rolloverData } = this.state;
        if (!rolloverData || !rolloverData.bonuses) {
            return [];
        }

        return rolloverData.bonuses.map(campaign => {
            const associatedRollovers = rolloverData.rollovers.filter(
                rollover => rollover.wallet.bonus_type === campaign.id
            );
            const totalActiveBonus = associatedRollovers.reduce(
                (sum, rollover) => sum + rollover.wallet.saldo,
                0
            );
            return {
                ...campaign,
                rollovers: associatedRollovers,
                totalActiveBonus,
            };
        }).filter(campaign => campaign.rollovers.length > 0); // Filter out campaigns with no rollovers at this stage
    };

    render() {
        const { loading, error, rolloverData, userData } = this.state;

        if (!userData) {
            if (loading) {
                return <div class="p-4 text-center"><Trans>Loading user data...</Trans></div>;
            }
            // Optionally, redirect to login or show a message if user data is critical and not loaded
            // For now, returning null or a generic message if fetchData fails to set userData
            return <div class="p-4 text-center text-red-500"><Trans>User data not available. Please try logging in again.</Trans></div>;
        }

        const userCurrency = userData.account.currency;
        const processedCampaigns = this.getProcessedCampaigns();
        const hasActiveBonuses = processedCampaigns.length > 0;

        return (
            <div class="">
                <div class="overflow-x-auto user-menu-subnav">
                    <U.SubTabsContainer>
                        <U.Tab selected={true}><Trans>My Bonuses</Trans></U.Tab>
                    </U.SubTabsContainer>
                </div>

                <div class="max-w-md mx-auto pt-2">
                    {loading && (
                        <div class="p-4 text-center"><Trans>Loading bonus information...</Trans></div>
                    )}
                    {error && (
                        <div class="p-4 text-center text-red-500"><Trans>Error:</Trans> {error}</div>
                    )}
                    {!loading && !error && !hasActiveBonuses && (
                        <div class="p-4 text-center"><Trans>You have no active bonuses at the moment.</Trans></div>
                    )}

                    {!loading && !error && hasActiveBonuses && processedCampaigns.filter(campaign => campaign.rollovers.length > 0)
                        .map((campaign) => <CampaignCard
                            key={campaign.id}
                            campaign={campaign}
                            userCurrency={userCurrency}
                        />)}
                </div>
            </div>
        );
    }
}


const ProgressBar = ({ percentage }: ProgressBarProps) => {
    const cappedPercentage = Math.min(100, Math.max(0, percentage));
    let progressBarColor = "bg-sky-500";
    if (cappedPercentage < 30) {
        progressBarColor = "bg-red-500";
    } else if (cappedPercentage < 70) {
        progressBarColor = "bg-yellow-500";
    } else {
        progressBarColor = "bg-green-500";
    }

    return (
        <div class="w-full bg-slate-200 dark:bg-slate-600 rounded-full h-3.5 my-2 relative shadow-inner">
            <div
                class={`h-3.5 rounded-full ${progressBarColor} transition-all duration-500 ease-out flex items-center justify-center`}
                style={{ width: `${cappedPercentage}%` }}
            >
                <span class="text-xs text-base text-white px-1 leading-none">
                    {Math.round(cappedPercentage)}%
                </span>
            </div>
        </div>
    );
};

// RolloverDetailItem component
interface RolloverDetailItemProps {
    rollover: RolloverState;
    userCurrency: string;
    isExpandedView?: boolean; // Optional: true if part of an expanded list of multiple rollovers
}

const RolloverDetailItem = ({ rollover, userCurrency, isExpandedView = false }: RolloverDetailItemProps) => {
    return (
        <div class={`${isExpandedView ? "ml-2 md:ml-4 p-2 md:p-3 border-l-2 border-slate-300 dark:border-slate-500 bg-slate-50 dark:bg-slate-600 rounded-r" : "mt-2"} text-sm text-slate-700 dark:text-slate-300 space-y-1`}>
            {(!rollover.awarded || isExpandedView) && ( // Show current amount if not awarded OR if it's an expanded view (where initial amount is always shown as "Amount")
                <div>{isExpandedView && !rollover.awarded ? <Trans>Current Amount</Trans> : <Trans>Current Amount</Trans>}: <span class="text-base">{formatCurrency(rollover.wallet.saldo, userCurrency)}</span></div>
            )}
            <div>{isExpandedView ? <Trans>Amount</Trans>: <Trans>Initial Amount</Trans>}: <span class="text-base">{formatCurrency(rollover.initial_amount, userCurrency)}</span></div>
            <div><Trans>Expires:</Trans> <span class="text-base">{formatDateTime(new Date(rollover.expire_date * 1000))}</span></div>
            <div>
                <Trans>Status:</Trans>
                {rollover.awarded ?
                    <span class="text-base text-green-600 dark:text-green-400 ml-1"><Trans>Awarded</Trans></span> :
                    (<div class="w-full">
                        <ProgressBar percentage={Math.round(rollover.progress * 100)} />
                    </div>)
                }
            </div>
        </div>
    );
};

// CampaignCard component
interface CampaignCardProps {
    campaign: ProcessedCampaign;
    userCurrency: string;
}

const CampaignCard = ({ campaign, userCurrency }: CampaignCardProps) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const toggleExpansion = () => {
        if (campaign.rollovers.length > 1) {
            setIsExpanded(!isExpanded);
        }
    };

    if (campaign.rollovers.length === 0) {
        return null; // Don't render campaigns with no active rollovers
    }

    return (
        <div key={campaign.id} class="bg-white dark:bg-slate-800 p-4 md:p-5 mb-4 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700">
            <div class="flex justify-between items-center">
                <h3 class="text-xl font-bold text-slate-700 dark:text-slate-100">{campaign.name}</h3>
                {campaign.rollovers.length > 1 && (
                    <button
                        onClick={toggleExpansion}
                        class="ml-2 focus:outline-hidden p-1 rounded-full hover:bg-slate-200 dark:hover:bg-slate-600"
                        aria-label={isExpanded ? "Collapse" : "Expand"}
                    >
                        {isExpanded ?
                            <UpCaretIcon class="transform rotate-180 h-5 w-5 inline-block text-slate-600 dark:text-slate-400" /> :
                            <UpCaretIcon class="transform rotate-90 h-5 w-5 inline-block text-slate-600 dark:text-slate-400" />
                        }
                    </button>
                )}
            </div>

            {campaign.rollovers.length === 1 && (
                <RolloverDetailItem rollover={campaign.rollovers[0]} userCurrency={userCurrency} />
            )}

            {campaign.rollovers.length > 1 && (
                <div class="mt-2 text-sm text-slate-700 dark:text-slate-300">
                    <div><Trans>Total Active Bonus:</Trans> <span class="text-base">{formatCurrency(campaign.totalActiveBonus, userCurrency)}</span></div>
                </div>
            )}

            {campaign.rollovers.length > 1 && isExpanded && (
                <div class="mt-3 space-y-3">
                    {campaign.rollovers.map(rollover => (
                        <RolloverDetailItem key={rollover.wallet_id} rollover={rollover} userCurrency={userCurrency} isExpandedView={true} />
                    ))}
                </div>
            )}
        </div>
    );
};


const getMockRolloverData = (): RolloverInfo => {
    const now = Math.floor(Date.now() / 1000);
    const oneDay = 24 * 60 * 60;
    return {
        bonuses: [
            { id: 1, name: "Registration Bonus" },
            { id: 2, name: "Daily Casino Bonus" },
            { id: 3, name: "Weekend Sports Reload" }, // This campaign will have no rollovers
            { id: 4, name: "Loyalty Points Exchange" } // This campaign will have one awarded rollover
        ],
        rollovers: [
            {
                wallet_id: 101, progress: 0.75, expire_date: now + (7 * oneDay), initial_amount: 100,
                tot_in: 750, tot_out: 0, awarded: false, wallet: { wallet_id: 101, bonus: true, bonus_type: 1, saldo: 75 }
            },
            {
                wallet_id: 201, progress: 1, expire_date: now - oneDay, initial_amount: 20,
                tot_in: 200, tot_out: 20, awarded: true, wallet: { wallet_id: 201, bonus: true, bonus_type: 2, saldo: 0 }
            },
            {
                wallet_id: 202, progress: 0.5, expire_date: now + (2 * oneDay), initial_amount: 50,
                tot_in: 250, tot_out: 0, awarded: false, wallet: { wallet_id: 202, bonus: true, bonus_type: 2, saldo: 25 }
            },
            {
                wallet_id: 203, progress: 0.2, expire_date: now + (3 * oneDay), initial_amount: 30,
                tot_in: 60, tot_out: 0, awarded: false, wallet: { wallet_id: 203, bonus: true, bonus_type: 2, saldo: 6 }
            },
            { // Rollover for Loyalty Points Exchange (awarded)
                wallet_id: 401, progress: 1, expire_date: now + (30 * oneDay), initial_amount: 10,
                tot_in: 100, tot_out: 10, awarded: true, wallet: { wallet_id: 401, bonus: true, bonus_type: 4, saldo: 0 }
            }
            // Note: No rollovers for campaign.id = 3 (Weekend Sports Reload)
        ]
    };
};